/**
 * A janela de linhas que vale a pena ter no DOM (A1 — gerenciador leve).
 *
 * O gerenciador desenhava todas as subpastas de uma pasta, e o custo é por
 * card: ~0,1 ms cada num Mac rápido, linear — 306 ms para entrar numa pasta
 * com 3.000 subpastas, 880 ms com 10.000, e o mesmo de novo a cada refresh de
 * fundo (medido em 16/10/2026, `reports/gerenciador-lento-2026-10-16`). Só
 * as linhas perto do viewport precisam existir; o resto vira espaço em
 * branco do tamanho certo, para a barra de rolagem e a posição não mudarem.
 *
 * Este módulo é só a conta, sem DOM, para ser testável com `node --test`.
 * A grade tem linhas uniformes (cards quadrados + `gap`); a lista tem duas
 * alturas (linha de pasta e linha de arte), por isso os **segmentos**.
 */

export interface RowSegment {
  /** Quantas linhas consecutivas têm esta altura. */
  count: number;
  /** Altura da linha, em px. */
  height: number;
  /** Espaço entre esta linha e a seguinte, em px (o `gap` da grade; 0 na lista). */
  gap: number;
}

export interface VirtualWindow {
  /** Primeira e última linha a desenhar (inclusive); `last < first` quando não há linhas. */
  first: number;
  last: number;
  /** Espaço a reservar antes da primeira linha desenhada (padding-top). */
  before: number;
  /** Espaço a reservar depois da última linha desenhada (padding-bottom). */
  after: number;
  /** Total de linhas. */
  rows: number;
}

function totalRows(segments: readonly RowSegment[]): number {
  let rows = 0;
  for (const segment of segments) rows += Math.max(0, segment.count);
  return rows;
}

/** Onde começa a linha `row` (0 = topo da primeira linha). Aceita `row === rows` (= fim). */
export function rowOffset(segments: readonly RowSegment[], row: number): number {
  let offset = 0;
  let remaining = Math.max(0, row);
  for (const segment of segments) {
    const count = Math.max(0, segment.count);
    const taken = Math.min(count, remaining);
    offset += taken * (segment.height + segment.gap);
    remaining -= taken;
    if (remaining === 0) break;
  }
  return offset;
}

/** A linha que contém a posição `y`; fora do conteúdo, a primeira ou a última. */
export function rowAt(segments: readonly RowSegment[], y: number): number {
  const rows = totalRows(segments);
  if (rows === 0) return 0;
  if (y <= 0) return 0;
  let start = 0;
  let firstRow = 0;
  for (const segment of segments) {
    const count = Math.max(0, segment.count);
    const pitch = segment.height + segment.gap;
    const end = start + count * pitch;
    if (y < end && pitch > 0) {
      return firstRow + Math.min(count - 1, Math.floor((y - start) / pitch));
    }
    start = end;
    firstRow += count;
  }
  return rows - 1;
}

/** Altura real do conteúdo: cada linha seguida do seu gap, menos o gap depois da última. */
export function contentHeight(segments: readonly RowSegment[]): number {
  const rows = totalRows(segments);
  if (rows === 0) return 0;
  let lastGap = 0;
  for (const segment of segments) if (segment.count > 0) lastGap = segment.gap;
  return rowOffset(segments, rows) - lastGap;
}

export function visibleRows(options: {
  /** `scrollTop` do contêiner que rola. */
  scrollTop: number;
  /** Altura visível do contêiner que rola. */
  viewportHeight: number;
  /** Distância do topo do conteúdo rolável até a primeira linha. */
  offsetTop: number;
  segments: readonly RowSegment[];
  /** Linhas de folga de cada lado, para a rolagem não mostrar branco. */
  overscan?: number;
}): VirtualWindow {
  const { segments } = options;
  const overscan = Math.max(0, options.overscan ?? 2);
  const rows = totalRows(segments);
  if (rows === 0) return { first: 0, last: -1, before: 0, after: 0, rows: 0 };

  const top = options.scrollTop - options.offsetTop;
  const bottom = top + Math.max(0, options.viewportHeight);
  const first = Math.max(0, rowAt(segments, top) - overscan);
  const last = Math.min(rows - 1, rowAt(segments, Math.max(top, bottom - 1)) + overscan);

  const total = contentHeight(segments);
  const before = rowOffset(segments, first);
  const rendered = rowOffset(segments, last + 1) - before - gapOf(segments, last);
  const after = Math.max(0, total - before - rendered);
  return { first, last, before, after, rows };
}

function gapOf(segments: readonly RowSegment[], row: number): number {
  let firstRow = 0;
  for (const segment of segments) {
    const count = Math.max(0, segment.count);
    if (row < firstRow + count) return segment.gap;
    firstRow += count;
  }
  return 0;
}

/** Quantas linhas `items` ocupam com `columns` por linha. */
export function rowCount(items: number, columns: number): number {
  const perRow = Math.max(1, Math.floor(columns));
  return Math.ceil(Math.max(0, items) / perRow);
}
